import ja from "date-fns/locale/ja"
import { Provider } from "mobx-react"
import moment from "moment"
import App, { AppContext } from "next/app"
import Head from "next/head"
import Router from "next/router"
import { SnackbarProvider } from "notistack"
import React from "react"
import { registerLocale, setDefaultLocale } from "react-datepicker"
import TagManager from "react-gtm-module"

import "~/../lib/css/common.css"
import "~/../lib/css/react-slick.css"
import { GTM_ID, pushUserIdToDataLayer } from "~/services/ga"
import { canonicalUrl } from "~/services/helper"
import RootStore from "~/stores/RootStore"

registerLocale("ja", ja)
setDefaultLocale("ja")

interface AppProps {
  Component: React.Component
  pageProps: any
  initialStoreData: any
}

export default class MyApp extends App<AppProps> {
  mobxStore: any

  // 最初のページロード時には、getInitialPropsはサーバー側でのみ実行される
  // クライアントサイドで実行されるのは、別のルートからLinkコンポーネントまたはルーティングAPIで移動してきたときだけ
  // getInitialProps() は、pagesディレクトリ以下のコンポーネントでのみ機能する
  static async getInitialProps({ Component, ctx }: AppContext) {
    const initialStoreData = new RootStore()
    // TODO: 今のところ空処理なので、不要であれば消す
    await initialStoreData.initializeData()
    let pageProps = {}
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps({ ...ctx, initialStoreData } as any)
    }
    
    return {
      pageProps,
      initialStoreData,
    }
  }

  constructor(props: any) {
    super(props)
    moment.locale("ja")
    // これをしないとフロントエンドでStoreデータにアクセスできない
    const isServer = typeof window === "undefined"
    this.mobxStore = isServer ? props.initialStoreData : RootStore.initializeFromProps(props.initialStoreData)
  }

  async componentDidMount() {
    TagManager.initialize({ gtmId: GTM_ID })
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: "optimize.activate" })

    await this.mobxStore.userStore.fetchLoginUser()
    const user = this.mobxStore.userStore.user
    if (user) {
      pushUserIdToDataLayer(user.id)
    }
    Router.events.on("routeChangeComplete", () => {
      // trackPageView(url)
      if (user) {
        pushUserIdToDataLayer(user.id)
      }
    })
  }

  render() {
    const { Component, pageProps, router } = this.props

    return (
      <>
        <Head>
          <link
            rel="stylesheet"
            href="https://cdnjs.cloudflare.com/ajax/libs/react-datepicker/2.14.1/react-datepicker.min.css"
          />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover"
          />
          <link key="canonical" rel="canonical" href={canonicalUrl(router.asPath)} />
        </Head>
        <Provider {...this.mobxStore}>
          <SnackbarProvider>
            <Component {...pageProps} />
          </SnackbarProvider>
        </Provider>
      </>
    )
  }
}
